import { Component, computed, effect, inject, input, signal, ChangeDetectorRef } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { PairingsService } from '../../../services/pairings/pairings.service';
import { LoggingService } from '../../../services/shared/logging/logging.service';
import { ToastrService } from 'ngx-toastr';

interface PairingSettings {
  numberOfPlayers: number;
  numberOfAPlayers: number;
  numberOfCPlayers: number;
  playersPerGroup: number;
  numberOfTees: number;
  numberOfWaves: number;
  numberOfBlocks: number;
  extraGroupsPerWave: string;
  extraGroupsPerTee: string;
  firstWaveStartTime: string;
  secondWaveStartTime: string;
  intervalMinutes: number;
  groupPlacement: string;
  numberSequence: string;
  startingGroupNumber: number;
  flipFlop: boolean;
  repairByScore: boolean;
  leadersOff10: boolean;
  invertedType: string;
}

@Component({
  selector: 'app-pairing-settings',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './pairing-settings.component.html',
  styleUrls: ['./pairing-settings.component.scss']
})
export class PairingSettingsComponent {
  private readonly pairingsService = inject(PairingsService);
  private readonly logger = inject(LoggingService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly toastr = inject(ToastrService);

  roundTournamentCourseId = input.required<number>();

  // Settings form model - use plain object for proper ngModel reactivity
  currentSettings: PairingSettings = {
    numberOfPlayers: 0,
    numberOfAPlayers: 0,
    numberOfCPlayers: 0,
    playersPerGroup: 3,
    numberOfTees: 2,
    numberOfWaves: 1,
    numberOfBlocks: 1,
    extraGroupsPerWave: 'Even',
    extraGroupsPerTee: 'Even',
    firstWaveStartTime: '07:00',
    secondWaveStartTime: '',
    intervalMinutes: 11,
    groupPlacement: 'BA|AB',
    numberSequence: 'Sequential',
    startingGroupNumber: 1,
    flipFlop: false,
    repairByScore: false,
    leadersOff10: false,
    invertedType: 'All Normal'
  };

  private readonly aPlayersErrorSignal = signal<string | null>(null);
  readonly aPlayersError = this.aPlayersErrorSignal.asReadonly();

  // Computed values as getters for reactivity
  get calculatedGroups(): number {
    if (this.currentSettings.numberOfPlayers === 0 || this.currentSettings.playersPerGroup === 0) return 0;
    return Math.ceil(this.currentSettings.numberOfPlayers / this.currentSettings.playersPerGroup);
  }

  get calculatedBlocks(): number {
    return this.currentSettings.numberOfTees * this.currentSettings.numberOfWaves;
  }

  get secondWaveEnabled(): boolean {
    return this.currentSettings.numberOfWaves === 2;
  }

  // Load settings from API when component initializes
  constructor() {
    effect(() => {
      const rtcId = this.roundTournamentCourseId();
      if (rtcId) {
        this.loadSettings(rtcId);
      }
    });
  }

  loadSettings(roundTournamentCourseId: number): void {
    this.logger.info('PairingSettingsComponent', `Loading settings for RTC ${roundTournamentCourseId}`);
    
    // For now, load from the pairings response which includes settings
    const state = this.pairingsService.state();
    if (state.pairings?.settings) {
      const apiSettings = state.pairings.settings;
      
      // Auto-populate Players in Field from available players count if not set
      let numberOfPlayers = apiSettings.numberOfPlayers ?? 0;
      if (numberOfPlayers === 0 && state.pairings.availablePlayers) {
        numberOfPlayers = state.pairings.availablePlayers.length;
        this.logger.info('PairingSettingsComponent', `Auto-set numberOfPlayers to ${numberOfPlayers} from available player count`);
      }
      
      // Calculate second wave start time with legacy behavior:
      // If waves = 2 and secondWaveStartTime is null/empty, default to 11:00
      const numberOfWaves = apiSettings.numberOfWaves ?? 1;
      let secondWaveTime = this.formatTime(apiSettings.secondWaveStartTime);
      if (numberOfWaves === 2 && !secondWaveTime) {
        secondWaveTime = '11:00';
        this.logger.info('PairingSettingsComponent', 'Auto-set secondWaveStartTime to 11:00 (2 waves, no value in DB)');
      }
      
      this.currentSettings = {
        numberOfPlayers,
        numberOfAPlayers: apiSettings.numberOfAPlayers ?? 0,
        numberOfCPlayers: apiSettings.numberOfCPlayers ?? 0,
        playersPerGroup: apiSettings.numberOfPlayersPerGroup ?? 3,
        numberOfTees: apiSettings.numberOfTees ?? 2,
        numberOfWaves,
        numberOfBlocks: this.calculatedBlocks,
        extraGroupsPerWave: this.mapHandleOddValue(apiSettings.handleOddNumberGroupsPerWave),
        extraGroupsPerTee: this.mapHandleOddValue(apiSettings.handleOddNumberGroupsPerBlock),
        firstWaveStartTime: this.formatTime(apiSettings.startTime),
        secondWaveStartTime: secondWaveTime,
        intervalMinutes: apiSettings.teeTimeInterval ?? 11,
        groupPlacement: apiSettings.aGroupPlacement ?? 'BA|AB',
        numberSequence: apiSettings.groupNumberSequence ?? 'Sequential',
        startingGroupNumber: apiSettings.startingGroupNumber ?? 1,
        flipFlop: apiSettings.repairByFlipFlop ?? false,
        repairByScore: apiSettings.repairByScore ?? false,
        leadersOff10: apiSettings.leadersOff10 ?? false,
        invertedType: apiSettings.invertedType ?? 'All Normal'
      };

      this.validateNumberOfAPlayers();
    }
  }

  saveSettings(): void {
    const rtcId = this.roundTournamentCourseId();
    
    this.logger.info('PairingSettingsComponent', 'Saving settings', this.currentSettings);

    this.validateNumberOfAPlayers();

    if (this.aPlayersError()) {
      this.toastr.error(this.aPlayersError() ?? "'A' players must be divisible by 6 before saving.");
      return;
    }
    
    // TODO: Implement save settings API endpoint
    // For now, this is a placeholder
    this.pairingsService.updateSettings(rtcId, {
      Number_Of_Players: this.currentSettings.numberOfPlayers.toString(),
      Number_Of_A_Players: this.currentSettings.numberOfAPlayers.toString(),
      Number_Of_C_Players: this.currentSettings.numberOfCPlayers.toString(),
      Number_Of_Players_Per_Group: this.currentSettings.playersPerGroup.toString(),
      Number_Of_Tees: this.currentSettings.numberOfTees.toString(),
      Number_Of_Waves: this.currentSettings.numberOfWaves.toString(),
      Tee_Time_Interval: this.currentSettings.intervalMinutes.toString(),
      Start_Time: this.currentSettings.firstWaveStartTime,
      Second_Wave_Start_Time: this.currentSettings.secondWaveStartTime || null,
      A_Group_Placement: this.currentSettings.groupPlacement,
      Group_Number_Sequence: this.currentSettings.numberSequence,
      Starting_Group_Number: this.currentSettings.startingGroupNumber.toString(),
      Repair_By_FlipFlop: this.currentSettings.flipFlop.toString(),
      Repair_By_Score: this.currentSettings.repairByScore.toString(),
      Leaders_Off_10: this.currentSettings.leadersOff10.toString(),
      Inverted_Type: this.currentSettings.invertedType,
      Handle_Odd_Number_Groups_Per_Wave: this.mapHandleOddToValue(this.currentSettings.extraGroupsPerWave).toString(),
      Handle_Odd_Number_Groups_Per_Block: this.mapHandleOddToValue(this.currentSettings.extraGroupsPerTee).toString()
    }).subscribe();
  }

  onPlayersChange(): void {
    this.validateNumberOfAPlayers();
    this.cdr.markForCheck();
  }

  onPlayersPerGroupChange(): void {
    this.cdr.markForCheck();
  }

  onTeesChange(): void {
    this.cdr.markForCheck();
  }

  onWavesChange(waves: number): void {
    this.logger.info('PairingSettingsComponent', `Waves changed to: ${waves}, type: ${typeof waves}`);
    if (waves === 1) {
      this.currentSettings.secondWaveStartTime = '';
    } else if (waves === 2 && !this.currentSettings.secondWaveStartTime) {
      this.currentSettings.secondWaveStartTime = '11:00';
    }
    this.logger.info('PairingSettingsComponent', `After change - numberOfWaves: ${this.currentSettings.numberOfWaves}, secondWaveStartTime: ${this.currentSettings.secondWaveStartTime}`);
    this.cdr.markForCheck();
  }

  onAPlayersChange(value: number | null | undefined): void {
    const parsedValue = typeof value === 'number' ? value : Number(value ?? 0);
    this.currentSettings.numberOfAPlayers = Number.isFinite(parsedValue) ? parsedValue : 0;
    this.validateNumberOfAPlayers();
    this.cdr.markForCheck();
  }

  private formatTime(date: Date | string | null | undefined): string {
    if (!date) return '';
    const d = typeof date === 'string' ? new Date(date) : date;
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  }

  private mapHandleOddValue(value: number | undefined): string {
    if (value === undefined || value === 0) return 'Even';
    if (value === 1) return 'Odd';
    return 'Even';
  }

  private mapHandleOddToValue(option: string): number {
    return option === 'Odd' ? 1 : 0;
  }

  private validateNumberOfAPlayers(): void {
    const totalPlayers = this.currentSettings.numberOfPlayers ?? 0;
    const aPlayers = this.currentSettings.numberOfAPlayers ?? 0;

    if (!Number.isFinite(aPlayers) || aPlayers <= 0) {
      this.aPlayersErrorSignal.set("'A' players must be greater than 0 and divisible by 6.");
      return;
    }

    if (aPlayers % 6 !== 0) {
      this.aPlayersErrorSignal.set("'A' players must be divisible by 6.");
      return;
    }

    if (totalPlayers > 0 && aPlayers > totalPlayers) {
      this.aPlayersErrorSignal.set("'A' players cannot exceed the total players in the field.");
      return;
    }

    this.aPlayersErrorSignal.set(null);
  }
}
